import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { usePos, useRegister } from "@/app/context";
import { Modal } from "@/components/Modal";
import { MoneyText } from "@/components/MoneyText";
import { useToast } from "@/components/Toast";
import { CustomerModal } from "@/features/sale/CustomerModal";
import { useCart } from "@/features/sale/hooks/useCart";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import type { Hold, HoldCreate } from "@/lib/types";

const LAYAWAY_DAYS = [1, 3, 7, 14, 30] as const;
const MINUTES_PER_DAY = 1440;

/**
 * ⏸ 保留 (design/05 §3.1, T07-28): 一時保留 (今日中 — default: 2 taps from the sale screen) or
 * 取り置き for a customer with a date (+ 手付金 in cash when the shop allows it). The basket is
 * kept at the server with its prices; the sale screen starts a new one at once. A 取り置き longer
 * than the shop's limit asks the manager (the server says so).
 */
export function HoldModal({
  open,
  onClose,
  onHeld,
}: {
  open: boolean;
  onClose: () => void;
  onHeld: (hold: Hold) => void;
}) {
  const { t } = useTranslation();
  const { api, printer } = usePos();
  const register = useRegister();
  const cart = useCart();
  const toast = useToast();
  const { withApproval } = useApproval();
  const defaultDays = register.data?.rules.layawayDefaultDays ?? 7;
  const depositAllowed = register.data?.rules.layawayDepositEnabled ?? false;
  const [kind, setKind] = useState<"park" | "layaway">("park");
  const [days, setDays] = useState<number>(defaultDays);
  const [note, setNote] = useState("");
  const [deposit, setDeposit] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerOpen, setCustomerOpen] = useState(false);
  const customer = cart.cart?.customer ?? null;
  const depositAmount = Number.parseInt(deposit || "0", 10);
  const needsCustomer = kind === "layaway" && !customer?.id;

  const submit = async () => {
    if (!cart.cartId || busy || needsCustomer) {
      return;
    }
    setBusy(true);
    setError(null);
    const body: HoldCreate = {
      kind,
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(kind === "layaway"
        ? {
            ttlMinutes: days * MINUTES_PER_DAY,
            ...(customer?.id ? { customerId: customer.id } : {}),
            ...(depositAllowed && depositAmount > 0
              ? { deposit: { method: "cash" as const, amount: depositAmount } }
              : {}),
          }
        : {}),
    };
    const cartId = cart.cartId;
    try {
      const hold = await withApproval(
        (token) =>
          call(() =>
            api.POST("/v1/pos/carts/{cart_id}/hold", {
              params: { path: { cart_id: cartId } },
              body,
              headers: token ? { "X-Approval-Token": token } : {},
            }),
          ),
        { subject: cartId, amount: depositAmount || null },
      );
      if (hold.deposit && hold.deposit.method === "cash" && printer.config.drawer) {
        await printer.queue.openDrawer();
      }
      await cart.startNew();
      setNote("");
      setDeposit("");
      setKind("park");
      toast.show(t("holds.held_toast"), "success");
      onHeld(hold);
    } catch (err) {
      setError(err instanceof ApprovalCancelled ? t("approval.cancelled") : errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        open={open && !customerOpen}
        title={t("holds.modal_title")}
        description={t("holds.modal_help")}
        onClose={onClose}
        busy={busy}
        footer={
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="min-h-pos-touch rounded-md border px-6 text-lg"
            >
              {t("app.cancel")}
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy || needsCustomer || !cart.cartId}
              className="min-h-pos-touch rounded-md bg-primary px-8 text-lg font-semibold text-primary-foreground disabled:opacity-50"
            >
              {busy ? t("app.saving") : t("holds.submit")}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="flex items-baseline justify-between text-lg">
            <span>{t("holds.basket", { count: cart.cart?.totals.itemCount ?? 0 })}</span>
            <MoneyText amount={cart.cart?.totals.total ?? 0} size="lg" />
          </p>
          <fieldset className="grid grid-cols-2 gap-2">
            <legend className="mb-1 font-medium">{t("holds.kind")}</legend>
            <button
              type="button"
              aria-pressed={kind === "park"}
              onClick={() => setKind("park")}
              className="min-h-pos-touch rounded-md border px-3 text-left aria-pressed:border-primary aria-pressed:bg-accent"
            >
              <span className="block text-lg font-semibold">{t("holds.kind_park")}</span>
              <span className="text-sm text-muted-foreground">{t("holds.kind_park_help")}</span>
            </button>
            <button
              type="button"
              aria-pressed={kind === "layaway"}
              onClick={() => setKind("layaway")}
              className="min-h-pos-touch rounded-md border px-3 text-left aria-pressed:border-primary aria-pressed:bg-accent"
            >
              <span className="block text-lg font-semibold">{t("holds.kind_layaway")}</span>
              <span className="text-sm text-muted-foreground">{t("holds.kind_layaway_help")}</span>
            </button>
          </fieldset>
          {kind === "layaway" ? (
            <>
              <div className="flex items-center justify-between gap-2 rounded-md border p-3">
                <span>
                  {t("holds.customer")}:{" "}
                  {customer
                    ? (customer.name ?? customer.phone ?? customer.email)
                    : t("holds.no_customer")}
                </span>
                <button
                  type="button"
                  onClick={() => setCustomerOpen(true)}
                  className="min-h-pos-touch rounded-md border px-4"
                >
                  {t("holds.choose_customer")}
                </button>
              </div>
              {needsCustomer ? (
                <p role="alert" className="text-pos-problem">
                  {t("holds.customer_required")}
                </p>
              ) : null}
              <fieldset>
                <legend className="mb-1 font-medium">{t("holds.days")}</legend>
                <div className="flex flex-wrap gap-2">
                  {LAYAWAY_DAYS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={days === d}
                      onClick={() => setDays(d)}
                      className="min-h-pos-touch min-w-20 rounded-md border px-3 text-lg aria-pressed:border-primary aria-pressed:bg-accent"
                    >
                      {t("holds.days_value", { count: d })}
                    </button>
                  ))}
                </div>
              </fieldset>
              {depositAllowed ? (
                <label className="flex flex-col gap-1">
                  <span className="font-medium">{t("holds.deposit")}</span>
                  <span className="text-sm text-muted-foreground">{t("holds.deposit_help")}</span>
                  <input
                    value={deposit}
                    onChange={(e) => setDeposit(e.target.value.replace(/\D/g, "").slice(0, 7))}
                    inputMode="numeric"
                    autoComplete="off"
                    className="min-h-pos-touch rounded-md border px-3 text-2xl tabular-nums"
                  />
                </label>
              ) : null}
            </>
          ) : null}
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("holds.note")}</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              placeholder={t("holds.note_placeholder")}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
          {error ? (
            <p role="alert" className="text-pos-problem">
              {error}
            </p>
          ) : null}
        </div>
      </Modal>
      <CustomerModal
        open={open && customerOpen}
        current={customer}
        onClose={() => setCustomerOpen(false)}
        onAttach={cart.attachCustomer}
        onDetach={cart.detachCustomer}
      />
    </>
  );
}
